// 📁 frontend/src/pages/AdminOrdersPage.js
import React, { useEffect, useState } from 'react';
import { fetchOrders, updateOrderStatus } from '../api/api';

function AdminOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [dateFilter, setDateFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [audio] = useState(() => new Audio('/sounds/beep.wav')); // ✅ صوت التنبيه
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('soundEnabled') === 'true';
  });

  // ✅ تحميل الطلبات من السيرفر
  useEffect(() => {
    const loadOrders = async () => {
      try {
        const ordersData = await fetchOrders();
        setOrders(ordersData);
      } catch (error) {
        console.error('❌ فشل في جلب الطلبات:', error);
      } finally {
        setLoading(false);
      }
    };
    loadOrders();
  }, []);

  // ✅ تفعيل الصوت بعد أول ضغطة فقط (مرّة واحدة)
  useEffect(() => {
    const enableSound = () => {
      audio.play().then(() => {
        audio.pause();
        audio.currentTime = 0;
        localStorage.setItem('soundEnabled', 'true');
        setSoundEnabled(true);
        console.log('🔊 تم تفعيل الصوت بنجاح بعد تفاعل المستخدم');
        window.removeEventListener('click', enableSound);
      }).catch(() => {});
    };

    if (!soundEnabled) {
      window.addEventListener('click', enableSound);
    }
    return () => window.removeEventListener('click', enableSound);
  }, [audio, soundEnabled]);

  // ✅ فحص الطلبات المعلقة أكثر من 5 دقائق
  useEffect(() => {
    if (!soundEnabled) return; // ما يعمل إلا بعد التفعيل

    const checkPendingOrders = () => {
      const now = new Date();
      const fiveMinutes = 5 * 60 * 1000;

      const hasOldPending = orders.some((order) => {
        if (order.status === 'pending') {
          const createdAt = new Date(order.created_at);
          return now - createdAt > fiveMinutes;
        }
        return false;
      });

      if (hasOldPending) {
        audio.play().catch(() => {
          console.log('🔇 لم يتم تشغيل الصوت (يتطلب تفاعل المستخدم)');
        });
      }
    };

    const interval = setInterval(checkPendingOrders, 30000); // كل 30 ثانية
    return () => clearInterval(interval);
  }, [orders, audio, soundEnabled]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o))
      );
    } catch (error) {
      console.error('❌ فشل في تحديث الحالة:', error);
    }
  };

  const filterBySearch = (order) => {
    const query = searchQuery.toLowerCase();
    return (
      (order.address && order.address.toLowerCase().includes(query)) ||
      (order.delivery_zone?.name &&
        order.delivery_zone.name.toLowerCase().includes(query)) ||
      order.phone.includes(query) ||
      String(order.id).includes(query)
    );
  };

  // ✅ الإحصائيات
  const stats = {
    total: orders.length,
    pending: orders.filter((o) => o.status === 'pending').length,
    confirmed: orders.filter((o) => o.status === 'confirmed').length,
    delivered: orders.filter((o) => o.status === 'delivered').length,
    canceled: orders.filter((o) => o.status === 'canceled').length,
  };

  const filteredOrders = orders
    .filter((order) => {
      const matchStatus = statusFilter === 'all' || order.status === statusFilter;

      const orderDate = new Date(order.created_at);
      const now = new Date();
      const diffDays = Math.floor((now - orderDate) / (1000 * 60 * 60 * 24));

      const matchDate =
        dateFilter === 'all' ||
        (dateFilter === 'today' && diffDays === 0) ||
        (dateFilter === '2days' && diffDays <= 1) ||
        (dateFilter === '3days' && diffDays <= 2) ||
        (dateFilter === '7days' && diffDays <= 6);

      return matchStatus && matchDate;
    })
    .filter(filterBySearch);

  return (
    <div style={styles.container}>
      <h2 style={styles.pageTitle}>🧾 إدارة الطلبات</h2>

      {/* 🔊 زر تفعيل الصوت */}
      <div style={{ textAlign: 'center', marginBottom: '15px' }}>
        {!soundEnabled ? (
          <button
            onClick={() => {
              audio.play().then(() => {
                audio.pause();
                audio.currentTime = 0;
                localStorage.setItem('soundEnabled', 'true');
                setSoundEnabled(true);
                alert('✅ تم تفعيل صوت التنبيهات بنجاح');
              });
            }}
            style={{
              backgroundColor: '#28a745',
              color: '#fff',
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            🔊 تفعيل صوت التنبيهات
          </button>
        ) : (
          <button
            onClick={() => {
              localStorage.setItem('soundEnabled', 'false');
              setSoundEnabled(false);
            }}
            style={{
              backgroundColor: '#dc3545',
              color: '#fff',
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            🔇 كتم التنبيهات
          </button>
        )}
      </div>

      {/* ✅ ملخص الإحصائيات */}
      <div style={styles.statsContainer}>
        <div style={{ ...styles.statCard, cursor: 'pointer' }} onClick={() => setStatusFilter('all')}>
          <p style={styles.statNumber}>{stats.total}</p>
          <p style={styles.statLabel}>📦 الكل</p>
        </div>
        <div style={{ ...styles.statCard, cursor: 'pointer' }} onClick={() => setStatusFilter('pending')}>
          <p style={{ ...styles.statNumber, color: '#d6a41f' }}>{stats.pending}</p>
          <p style={styles.statLabel}>🟡 قيد الانتظار</p>
        </div>
        <div style={{ ...styles.statCard, cursor: 'pointer' }} onClick={() => setStatusFilter('confirmed')}>
          <p style={{ ...styles.statNumber, color: '#28a745' }}>{stats.confirmed}</p>
          <p style={styles.statLabel}>🟢 تم التأكيد</p>
        </div>
        <div style={{ ...styles.statCard, cursor: 'pointer' }} onClick={() => setStatusFilter('delivered')}>
          <p style={{ ...styles.statNumber, color: '#0c6efd' }}>{stats.delivered}</p>
          <p style={styles.statLabel}>📦 تم التوصيل</p>
        </div>
        <div style={{ ...styles.statCard, cursor: 'pointer' }} onClick={() => setStatusFilter('canceled')}>
          <p style={{ ...styles.statNumber, color: '#e63946' }}>{stats.canceled}</p>
          <p style={styles.statLabel}>🔴 ملغى</p>
        </div>
      </div>

      {/* ✅ مربع البحث */}
      <input
        type="text"
        placeholder="🔍 بحث حسب العنوان، المنطقة، الهاتف، أو رقم الطلب..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={styles.searchInput}
      />

      {/* ✅ الفلاتر */}
      <div style={styles.filtersWrapper}>
        <div style={styles.filterGroup}>
          <label>🧭 الحالة:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={styles.select}
          >
            <option value="all">الكل</option>
            <option value="pending">🚧 قيد الانتظار</option>
            <option value="confirmed">✅ تم التأكيد</option>
            <option value="delivered">📦 تم التوصيل</option>
            <option value="canceled">❌ ملغى</option>
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label>🗓️ التاريخ:</label>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            style={styles.select}
          >
            <option value="all">الكل</option>
            <option value="today">اليوم</option>
            <option value="2days">آخر يومين</option>
            <option value="3days">آخر 3 أيام</option>
            <option value="7days">آخر 7 أيام</option>
          </select>
        </div>
      </div>

      {/* ✅ الطلبات */}
      {loading ? (
        <p style={{ textAlign: 'center' }}>⏳ جاري تحميل الطلبات...</p>
      ) : filteredOrders.length === 0 ? (
        <p style={{ textAlign: 'center' }}>⚠️ لا توجد طلبات مطابقة.</p>
      ) : (
        <div style={styles.cardsContainer}>
          {filteredOrders.map((order) => (
            <div key={order.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <span>
                  رقم الطلب: <strong>{order.id}</strong>
                </span>
                <span style={styles.statusText(order.status)}>
                  {order.status === 'pending' && '🟡 قيد الانتظار'}
                  {order.status === 'confirmed' && '🟢 تم التأكيد'}
                  {order.status === 'delivered' && '📦 تم التوصيل'}
                  {order.status === 'canceled' && '🔴 ملغى'}
                </span>
              </div>

              <div style={styles.cardBody}>
                {order.delivery_method === 'delivery' ? (
                  <>
                    <p>📍 <strong>المنطقة:</strong> {order.delivery_zone?.name || 'غير محدد'}</p>
                    <p>🏠 <strong>العنوان:</strong> {order.address || 'غير متوفر'}</p>
                  </>
                ) : (
                  <p>🍽️ <strong>طلب داخلي داخل المحل</strong></p>
                )}
                <p>📞 <strong>الهاتف:</strong> {order.phone}</p>
                <p>👤 <strong>الاسم:</strong> {order.customer_name || 'غير متوفر'}</p>

                <p>
                  <strong>الطريقة:</strong>{' '}
                  {order.delivery_method === 'delivery' ? '🚚 توصيل' : '🍽️ داخلي'}
                </p>

                <div style={styles.itemsContainer}>
                  {order.items.map((item, i) => (
                    <div key={i} style={styles.itemCard}>
                      <strong>{item.name}</strong> × {item.quantity}
                      <br />
                      <small>({item.total_price}₪)</small>
                      {item.selected_options &&
                        Object.values(item.selected_options).flat().length > 0 && (
                          <ul style={styles.optionsList}>
                            {Object.values(item.selected_options)
                              .flat()
                              .map((opt, k) => (
                                <li key={k} style={styles.optionItem}>
                                  {opt.name} ({opt.price_diff}₪)
                                </li>
                              ))}
                          </ul>
                        )}
                    </div>
                  ))}
                </div>

                {order.notes && <p>📝 <strong>ملاحظات:</strong> {order.notes}</p>}

                <p style={styles.totalPrice}>
                  <strong>الإجمالي:</strong> {order.total} ₪
                </p>

                <div style={styles.statusChanger}>
                  <select
                    value={order.status}
                    onChange={(e) => handleStatusChange(order.id, e.target.value)}
                    style={styles.statusSelect}
                  >
                    <option value="pending">🚧 قيد الانتظار</option>
                    <option value="confirmed">✅ تم التأكيد</option>
                    <option value="delivered">📦 تم التوصيل</option>
                    <option value="canceled">❌ ملغى</option>
                  </select>
                </div>

                <button
                  onClick={() => window.open(`/admin/orders/print/${order.id}`, '_blank')}
                  style={styles.printButton}
                >
                  🖨️ طباعة الفاتورة
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* 🎨 الستايلات */
const styles = {
  container: {
    direction: 'rtl',
    fontFamily: "'Cairo', sans-serif",
    padding: '20px',
    backgroundColor: '#f9f7f3',
    minHeight: '100vh',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: '20px',
    color: '#3f3d56',
  },
  statsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '12px',
    marginBottom: '15px',
  },
  statCard: {
    backgroundColor: '#fff',
    padding: '12px',
    borderRadius: '8px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
    textAlign: 'center',
  },
  statNumber: {
    fontSize: '24px',
    fontWeight: '700',
    margin: '0',
    color: '#3f3d56',
  },
  statLabel: {
    marginTop: '4px',
    fontSize: '14px',
    color: '#6b7280',
  },
  searchInput: {
    width: '100%',
    padding: '10px',
    marginBottom: '20px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    fontSize: '15px',
  },
  filtersWrapper: {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    marginBottom: '15px',
    flexWrap: 'wrap',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: '15px',
  },
  select: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    fontSize: '14px',
  },
  cardsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '15px',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.08)',
    padding: '15px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '10px',
    fontWeight: '600',
    color: '#3f3d56',
  },
  statusText: (status) => ({
    color:
      status === 'pending'
        ? '#d6a41f'
        : status === 'confirmed'
        ? '#28a745'
        : status === 'delivered'
        ? '#0c6efd'
        : '#e63946',
    fontWeight: '700',
  }),
  cardBody: {
    fontSize: '14px',
    color: '#3f3d56',
  },
  itemsContainer: {
    paddingTop: '10px',
  },
  itemCard: {
    backgroundColor: '#fffaf2',
    padding: '8px',
    borderRadius: '8px',
    marginBottom: '6px',
    fontSize: '13px',
  },
  optionsList: {
    paddingLeft: '15px',
    margin: '5px 0 0',
    listStyleType: 'square',
  },
  optionItem: {
    fontSize: '12px',
    color: '#666',
  },
  totalPrice: {
    marginTop: '15px',
    fontSize: '15px',
    fontWeight: '700',
    color: '#000',
  },
  statusChanger: {
    marginTop: '10px',
  },
  statusSelect: {
    width: '100%',
    padding: '8px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    fontSize: '14px',
  },
  printButton: {
    marginTop: '10px',
    width: '100%',
    padding: '8px',
    borderRadius: '6px',
    backgroundColor: '#333',
    color: '#fff',
    fontWeight: 'bold',
    cursor: 'pointer',
    border: 'none',
  },
};

export default AdminOrdersPage;
 